// MagicGenerateButton.js
//
// Megosztott "varázsló" generálás-gomb 3 állapottal:
//   - idle:       halvány/ghost (alig látszik) — semmi nincs kiválasztva
//   - selected:   brand-lila (#5e50f7) — kártya kiválasztva, készen indítható
//   - generating: helyben marad, körkörös spinner + finom fehér fény-csík
//                 az aktív gomb FELSŐ élén (1.4s loop), tömör lila háttér.
//
// Használat:
//   <MagicGenerateButton state="idle|selected|generating" label="..."
//                        onClick={...} disabled={...} testid="..." />
//
// A komponens minden vizuális/animációs réteget magában foglal — a hívó
// csak az állapotot és a labelt adja meg.

import React from "react";
import { CircleNotch } from "@phosphor-icons/react";

export default function MagicGenerateButton({
  state = "idle",
  label = "Generálás",
  onClick,
  disabled = false,
  testid = "magic-generate-button",
  width = "auto",
}) {
  const isIdle = state === "idle";
  const isSelected = state === "selected";
  const isGenerating = state === "generating";

  const clickable = !disabled && !isGenerating && (isSelected || isIdle && !!onClick);

  // Vizuális állapotok — méretezés és skin POSZT VARÁZSLÓ szerint
  // (padding/font/border-radius/minWidth/icon-size pixelszinten egyezik
  // a /agents/magnet-post Generate gombjával).
  const bg = isIdle
    ? "rgba(255,255,255,0.04)"
    : "linear-gradient(135deg, #7c6cf2, #5e4cd8)";
  const border = isIdle
    ? "1px solid rgba(255,255,255,0.08)"
    : "none";
  const color = isIdle ? "#6a6a80" : "#ffffff";
  const opacity = isGenerating ? 0.78 : 1;
  const cursor = isGenerating ? "wait" : (clickable ? "pointer" : "not-allowed");
  const shadow = !isIdle
    ? "0 8px 28px rgba(124,108,242,0.35), 0 0 0 1px #7c6cf255, inset 0 1px 0 rgba(255,255,255,0.18)"
    : "none";

  return (
    <>
      <style>{`
        @keyframes magicgen-shimmer {
          0%   { transform: translateX(-110%); }
          100% { transform: translateX(110%); }
        }
        @keyframes magicgen-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <button
        type="button"
        onClick={clickable ? onClick : undefined}
        disabled={!clickable && !isGenerating}
        data-testid={testid}
        data-state={state}
        style={{
          position: "relative",
          overflow: "hidden",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
          padding: "14px 24px",
          minWidth: width === "auto" ? 280 : undefined,
          width,
          borderRadius: 12,
          background: bg,
          border,
          color,
          fontSize: 15,
          fontWeight: 600,
          letterSpacing: "0.01em",
          fontFamily: "inherit",
          cursor,
          opacity,
          boxShadow: shadow,
          transition: "background 240ms ease, border-color 240ms ease, color 240ms ease, opacity 240ms ease, box-shadow 240ms ease",
        }}
      >
        {/* Top edge shimmer — csak generating állapotban */}
        {isGenerating && (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 0, left: 0,
              height: 2,
              width: "40%",
              background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.75) 50%, transparent 100%)",
              animation: "magicgen-shimmer 1.4s linear infinite",
              pointerEvents: "none",
            }}
          />
        )}
        {isGenerating ? (
          <CircleNotch
            size={16}
            weight="bold"
            style={{ animation: "magicgen-spin 0.85s linear infinite" }}
          />
        ) : null}
        <span style={{ position: "relative", zIndex: 1 }}>{label}</span>
      </button>
    </>
  );
}
